/**
 * ThemeIcon - Premium monochrome icons for themes
 * 
 * Apple-like, minimal design system.
 * Single source of truth for theme iconography.
 * 
 * Usage:
 *   <ThemeIcon themeKey="classical" />
 *   <ThemeIcon themeKey="sleep" size={24} />
 */

import React from 'react';
import { 
  Music, 
  Sparkles, 
  Headphones, 
  Leaf, 
  Moon, 
  Zap, 
  Wind,
  Circle
} from 'lucide-react';

// Theme key to icon mapping
const THEME_ICONS = {
  // Classical - Musical note
  classical: Music,
  classique: Music,
  
  // Ambient - Sparkles/stars
  ambient: Sparkles,
  piano_ambiant: Sparkles,
  
  // Chill / Lofi - Headphones
  chill_lofi: Headphones,
  chill: Headphones,
  lofi: Headphones,
  
  // Nature - Leaf
  nature: Leaf,
  
  // Sleep - Moon
  sleep: Moon,
  sommeil: Moon,
  
  // Pulse Flow - Lightning bolt
  pulse_flow: Zap,
  pulse: Zap,
  flow: Zap,
  focus: Zap,
  
  // Breathing & Meditation - Wind/breath
  breathing_meditation: Wind,
  breathing: Wind,
  respiration: Wind,
  meditation: Wind,
};

// Default fallback icon
const DEFAULT_ICON = Circle;

/**
 * Get the icon component for a theme key
 * @param {string} themeKey - Theme identifier
 * @returns {React.Component} - Lucide icon component
 */
export const getThemeIconComponent = (themeKey) => {
  if (!themeKey) return DEFAULT_ICON;
  
  // Normalize key: lowercase, trim
  const normalizedKey = themeKey.toLowerCase().trim();
  
  return THEME_ICONS[normalizedKey] || DEFAULT_ICON;
};

/**
 * ThemeIcon Component
 * Renders a monochrome, premium-style icon for a given theme
 */
const ThemeIcon = ({ 
  themeKey, 
  size = 20, 
  className = '',
  style = {}
}) => {
  const IconComponent = getThemeIconComponent(themeKey);
  
  return (
    <IconComponent 
      size={size}
      className={`text-white/90 ${className}`}
      style={{
        filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.15))',
        strokeWidth: 1.5,
        ...style
      }}
    />
  );
};

export default ThemeIcon;
